import React, { useMemo } from "react";
import { X, Download, Printer } from "lucide-react";

/**
 * Full breakdown of Total Consumed (OUT movements value) — every stock item
 * with its taken qty × snapshot cost for the current date/department scope.
 * Opened from the Reports "Total consumed" card. Mirrors the
 * StockValueDetailDialog UX (CSV + native browser Print for PDF).
 *
 * Data is passed in via `rows` (already scoped by the parent Reports page's
 * date range + department filter through the /reports/item-consumption call).
 */
const money = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/**
 * Compact requester list — "Sarah (5), Bob (3), Alice (1)". Cap at 3 to
 * keep rows scannable and append "+N more" when there are extras.
 */
const formatRequesters = (list) => {
  if (!Array.isArray(list) || list.length === 0) return "—";
  const top = list.slice(0, 3).map((r) => `${r.name} (${r.qty})`).join(", ");
  const rest = list.length - 3;
  return rest > 0 ? `${top} +${rest} more` : top;
};

const TotalConsumedDetailDialog = ({
  open,
  rows = [],
  department,
  rangeLabel,
  onClose,
}) => {
  const sortedRows = useMemo(
    () =>
      [...(rows || [])]
        .filter((r) => Number(r.spend || 0) > 0)
        .sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0)),
    [rows],
  );

  const total = useMemo(
    () => sortedRows.reduce((s, r) => s + Number(r.spend || 0), 0),
    [sortedRows],
  );

  if (!open) return null;

  const downloadCsv = () => {
    if (!sortedRows.length) return;
    const header =
      "Item,Type,Department,Taken,Unit,Sub-unit total,Sub-unit,Spend,Requesters";
    const csvRows = sortedRows.map((r) => {
      // Full requester breakdown (no +N more truncation) so the CSV is
      // useful for downstream analysis in Excel/Sheets.
      const requesters = Array.isArray(r.requesters) && r.requesters.length
        ? r.requesters.map((rq) => `${rq.name} (${rq.qty})`).join("; ")
        : "";
      return [
        `"${(r.name || "").replace(/"/g, '""')}"`,
        r.type || "",
        r.department || "",
        r.qty_pack ?? "",
        r.unit || "",
        r.pack_size && r.pack_unit ? r.qty_sub : "",
        r.pack_size && r.pack_unit ? r.pack_unit : "",
        Number(r.spend || 0).toFixed(2),
        `"${requesters.replace(/"/g, '""')}"`,
      ].join(",");
    });
    const csv = [
      header,
      ...csvRows,
      `,,,,,,,Total,`,
      `,,,,,,,${total.toFixed(2)},`,
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `total-consumed-${
      department && department !== "all" ? department : "all-depts"
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const scopeTitle =
    department && department !== "all"
      ? `${department} · value consumed`
      : "All departments · value consumed";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 print:bg-white print:static print:inset-auto print:block print:z-auto"
      data-testid="total-consumed-detail-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(960px,96vw)] max-h-[92vh] flex flex-col overflow-hidden print:max-h-none print:h-auto print:w-full print:overflow-visible print:shadow-none print:border-0 print:rounded-none"
        onClick={(e) => e.stopPropagation()}
        data-print-target="total-consumed"
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between gap-3 flex-wrap print:hidden">
          <div>
            <div className="label-eyebrow">
              Reports · Total consumed drill-down
              {rangeLabel ? ` · ${rangeLabel}` : ""}
            </div>
            <h3 className="font-display text-2xl">{scopeTitle}</h3>
            <div className="text-xs text-[#607087] mt-0.5">
              {sortedRows.length} item{sortedRows.length === 1 ? "" : "s"} ·
              sorted by spend
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="total-consumed-export-csv"
              onClick={downloadCsv}
              disabled={!sortedRows.length}
              className="nb-btn nb-btn-dark px-3 py-2 text-sm flex items-center gap-1.5 disabled:opacity-40"
            >
              <Download size={14} strokeWidth={2.5} /> CSV
            </button>
            <button
              data-testid="total-consumed-export-pdf"
              onClick={() => window.print()}
              className="nb-btn bg-white px-3 py-2 text-sm flex items-center gap-1.5"
              title="Print or Save as PDF"
            >
              <Printer size={14} strokeWidth={2.5} /> PDF
            </button>
            <button
              data-testid="total-consumed-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
              aria-label="close"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block px-5 py-4 border-b-2 border-black">
          <div className="label-eyebrow">Podlab · Total consumed</div>
          <h1 className="font-display text-3xl mt-1">{scopeTitle}</h1>
          <div className="text-sm mt-1">
            <b>Total:</b> {money(total)} · {sortedRows.length} item
            {sortedRows.length === 1 ? "" : "s"}
            {rangeLabel ? ` · ${rangeLabel}` : ""} · <b>Generated:</b>{" "}
            {new Date().toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </div>
        </div>

        <div className="p-5 border-b-2 border-black bg-[#FFF3E4] flex items-baseline justify-between print:bg-white">
          <div>
            <div className="label-eyebrow">Total value consumed (OUT)</div>
            <div
              className="font-mono-num font-bold text-4xl"
              data-testid="total-consumed-total"
            >
              {money(total)}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-clean print:overflow-visible">
          {sortedRows.length === 0 && (
            <div className="p-10 text-center text-[#607087] italic">
              No consumption recorded in this scope.
            </div>
          )}
          {sortedRows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-white border-b-2 border-black sticky top-0">
                <tr className="text-left">
                  <th className="py-2 px-3 font-display">Item</th>
                  <th className="py-2 px-3 font-display">Type</th>
                  <th className="py-2 px-3 font-display">Dept</th>
                  <th className="py-2 px-3 font-display text-right">Taken</th>
                  <th className="py-2 px-3 font-display text-right">
                    Sub-unit total
                  </th>
                  <th className="py-2 px-3 font-display text-right">Spend</th>
                  <th className="py-2 px-3 font-display">Requesters</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[#E0E5EA]"
                    data-testid={`total-consumed-row-${r.id}`}
                  >
                    <td className="py-2 px-3 font-display truncate max-w-[240px]">
                      {r.name}
                    </td>
                    <td className="py-2 px-3 text-[#607087]">{r.type}</td>
                    <td className="py-2 px-3 text-[#607087]">
                      {r.department || "—"}
                    </td>
                    <td className="py-2 px-3 font-mono-num text-right">
                      {r.qty_pack}{" "}
                      <span className="text-[#607087]">{r.unit}</span>
                    </td>
                    <td className="py-2 px-3 font-mono-num text-right text-[#9A6F00]">
                      {r.pack_size && r.pack_unit
                        ? `${Number(r.qty_sub).toLocaleString()} ${r.pack_unit}`
                        : "—"}
                    </td>
                    <td className="py-2 px-3 font-mono-num text-right font-bold">
                      {money(r.spend)}
                    </td>
                    <td
                      className="py-2 px-3 text-xs text-[#607087] max-w-[240px]"
                      data-testid={`total-consumed-requesters-${r.id}`}
                      title={
                        Array.isArray(r.requesters)
                          ? r.requesters
                              .map((rq) => `${rq.name} (${rq.qty})`)
                              .join(", ")
                          : ""
                      }
                    >
                      {formatRequesters(r.requesters)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TotalConsumedDetailDialog;
